/* Waiting on you (E1, HOME1). */
/* The folded line's region: no box of its own, the button inside is the line. */
.barRegion {
  position: relative; /* for Help's "Shown from Help" tag on its edge */
  margin: 0;
}

.bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 0 18px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
  color: var(--text-body);
  text-align: left;
  cursor: pointer;
}

.bar[data-hovered] {
  border-color: var(--brand-cyan);
}

.barDot {
  display: grid;
  flex: none;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.dotDone {
  background: var(--status-done-bg);
  color: var(--status-done-ink);
}

.dotWaiting {
  background: var(--status-waiting-bg);
  color: var(--status-waiting-ink);
}

.barTitle {
  color: var(--text-heading);
  font-size: 18px;
  letter-spacing: 0.02em;
}

.barSummary {
  min-width: 0;
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.barWithAction {
  flex-wrap: wrap;
  padding: 8px 8px 8px 18px;
  cursor: default;
}

.barWithAction .barTitle {
  margin: 0;
  font-weight: var(--fw-medium);
}

.barWithAction .barSummary {
  flex: 1 1 200px;
}

.barAction {
  flex: none;
  margin-left: auto;
}

.barToggleButton {
  min-height: var(--control-height);
  margin-left: 0;
  padding: 0 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
}

.barToggleButton[data-hovered],
.barToggleButton[data-focus-visible] {
  background: var(--gray-75);
}

.barToggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  color: var(--brand-navy);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 18px;
  padding: 14px 8px 14px 16px;
  border: 1px solid var(--border-hairline);
  border-radius: var(--radius-sm);
  background: var(--white);
}

.urgent {
  gap: 20px 24px;
  padding: 18px 20px;
  border-color: var(--border-urgent);
  background: var(--surface-urgent);
}

.setup {
  border-style: dashed;
  border-color: var(--border-dashed);
}

.icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--status-info-bg);
  color: var(--status-info-ink);
}

.urgent .icon {
  width: 56px;
  height: 56px;
  background: var(--status-waiting-bg);
  color: var(--status-waiting-ink);
}

/* Something's wrong with what's asked (an overage): a red edge and icon; the pill says what. */
.problem {
  border-color: var(--status-problem-ink);
  box-shadow: inset 4px 0 0 var(--status-problem-ink);
}

.problem .icon {
  background: var(--status-problem-bg);
  color: var(--status-problem-ink);
}

.text {
  display: flex;
  flex: 1 1 280px;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.urgent .text {
  flex-basis: 320px;
  gap: 6px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.eyebrow {
  color: var(--text-gold);
}

.title {
  margin: 0;
  color: var(--text-heading);
  font-size: 16px;
  font-weight: var(--fw-bold);
  line-height: 1.35;
}

.urgent .titleDisplay {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: var(--fw-medium);
  line-height: 1.15;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.detail {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.urgent .detail {
  font-size: 15px;
  line-height: 1.6;
}

/* APP-149 D2: how long it has waited, first on the line, in the line's own ink and semibold. */
.since {
  font-weight: var(--fw-semibold);
  white-space: nowrap;
}

/* APP-248: the dot between how long it has waited and the rest, drawn so it's seen but never part
   of the line's text; screen readers and a copy get the hidden full stop beside it instead. */
.sep::before {
  content: ' · ';
  white-space: pre;
}

/* The role tag, then the action and Dismiss: at the right of the row on a wide screen, and one
   line below the text on a narrow one (APP-054 decision 23). */
.actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: 4px;
}

.role {
  margin-right: 10px;
}

.dismiss {
  display: grid;
  flex: none;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.dismiss[data-hovered],
.dismiss[data-focus-visible] {
  background: var(--gray-75);
  color: var(--brand-navy);
}

.nothing {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  background: var(--gray-50);
  color: var(--text-secondary);
  font-size: 14px;
}

.nothingIcon {
  flex: none;
  color: var(--status-done-ink);
}

/* The header's right side: a short note ("Also emailed to the Executive group"), then Hide. */
.asideRow {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
  justify-content: flex-end;
}

.note {
  color: var(--text-secondary);
  font-size: 13px;
}

/* X1: notes for information only, under the list; never counted as waiting. */
.fyiList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fyi {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  background: var(--status-info-bg);
  color: var(--status-info-ink);
  font-size: 14px;
  line-height: 1.5;
}

.fyiIcon {
  display: inline-flex;
  flex: none;
  margin-top: 1px;
}

.fyiText {
  flex: 1 1 240px;
}

/* APP-149 D6: the Sort menu beside Hide, in the filter menus' look. */
.sortPopover {
  width: 300px;
  max-width: calc(100vw - 32px);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-menu);
}

.sortMenu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  outline: none;
}

.sortOption {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: var(--control-height);
  padding: 6px 12px 6px 8px;
  border-radius: var(--radius-sm);
  color: var(--text-body);
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.sortOption[data-focused],
.sortOption[data-hovered] {
  background: var(--brand-cyan-50);
  color: var(--brand-navy);
}

.sortOption[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.sortOption[data-selected] {
  color: var(--brand-navy);
  font-weight: var(--fw-semibold);
}

.sortTick {
  display: inline-flex;
  flex: none;
  width: 16px;
  color: var(--brand-petrol);
}

.sortWords {
  display: flex;
  flex: 1;
  flex-direction: column;
}

/* Secondary ink, not muted: it keeps 4.5:1 on the focused choice's tint too. */
.sortSub {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: var(--fw-regular);
}

.sortFoot {
  margin: 0;
  padding: 10px 14px 12px;
  border-top: 1px solid var(--border-hairline);
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.45;
}
